import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { hasPermission } from '../lib/permissions';
import CameraFileInput from './CameraFileInput';
import ProtectedMediaThumbnail from './ProtectedMediaThumbnail';
import { openProtectedFile } from '../lib/protectedFiles';
import { appTodayDate, buildAppDateTimeIso, createClientRequestId } from '../lib/appDateTime';
import { 
  UtensilsCrossed, 
  Clock, 
  Calendar, 
  FileText, 
  Plus, 
  Download, 
  AlertTriangle, 
  CheckCircle2, 
  XCircle, 
  MinusCircle,
  Syringe,
  Filter,
  ClipboardList,
  Loader2,
  Trash2,
  Edit,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || '';

// Food types commonly used for wildlife
const FOOD_TYPES = [
  'Frutas',
  'Legumes',
  'Ração',
  'Carne',
  'Peixe',
  'Insetos',
  'Néctar',
  'Papinha',
  'Suplemento',
  'Outro'
];

const FEEDING_FORMS = [
  { value: 'Sólida', label: 'Sólida' },
  { value: 'Pastosa', label: 'Pastosa' },
  { value: 'Líquida', label: 'Líquida' },
  { value: 'Assistida/Sonda', label: 'Assistida/Sonda' },
  { value: 'Outro', label: 'Outro' }
];

const ADMINISTRATION_TYPES = [
  { value: 'Voluntária', label: 'Voluntária' },
  { value: 'Forçada', label: 'Forçada' }
];

const FEEDING_STATUS = [
  { value: 'Oferecida', label: 'Oferecida', color: 'bg-gray-100 text-gray-700' },
  { value: 'Aceita', label: 'Aceita', color: 'bg-green-100 text-green-700' },
  { value: 'Parcial', label: 'Parcial', color: 'bg-yellow-100 text-yellow-700' },
  { value: 'Recusada', label: 'Recusada', color: 'bg-red-100 text-red-700' }
];

const UNITS = [
  { value: 'g', label: 'gramas (g)' },
  { value: 'ml', label: 'mililitros (ml)' },
  { value: 'unidade', label: 'unidade(s)' },
  { value: 'outro', label: 'outro' }
];

const getApiErrorMessage = (error, fallback) => {
  const detail = error.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((item) => item?.msg || item?.message || item?.detail)
      .filter(Boolean)
      .join('; ') || fallback;
  }
  return fallback;
};

export default function FeedingTab({ animalId, user }) {
  const [records, setRecords] = useState([]);
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showNewRecordForm, setShowNewRecordForm] = useState(false);
  const [showPlanForm, setShowPlanForm] = useState(false);
  const [summary, setSummary] = useState(null);
  const [summaryUnit, setSummaryUnit] = useState('g');
  const [editingRecordId, setEditingRecordId] = useState(null);
  const [responsibleUsers, setResponsibleUsers] = useState([]);
  
  // Filters
  const [filterStartDate, setFilterStartDate] = useState('');
  const [filterEndDate, setFilterEndDate] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  
  // New record form
  const [foodType, setFoodType] = useState('');
  const [quantity, setQuantity] = useState('');
  const [unit, setUnit] = useState('g');
  const [form, setForm] = useState('Sólida');
  const [administrationType, setAdministrationType] = useState('Voluntária');
  const [status, setStatus] = useState('Aceita');
  const [responsibleId, setResponsibleId] = useState('');
  const [observations, setObservations] = useState('');
  const [recordDate, setRecordDate] = useState('');
  const [recordTime, setRecordTime] = useState('');
  const [recordMediaFile, setRecordMediaFile] = useState(null);
  
  // Plan form
  const [planFoodType, setPlanFoodType] = useState('');
  const [planQuantity, setPlanQuantity] = useState('');
  const [planUnit, setPlanUnit] = useState('g');
  const [planForm, setPlanForm] = useState('Sólida');
  const [planFrequency, setPlanFrequency] = useState('3');
  const [planSchedules, setPlanSchedules] = useState(['08:00', '12:00', '17:00']);
  const [fastingStartAt, setFastingStartAt] = useState('');
  const [fastingEndAt, setFastingEndAt] = useState('');
  const [fastingProcedureName, setFastingProcedureName] = useState('');
  const [fastingReason, setFastingReason] = useState('');
  const [planResponsibleId, setPlanResponsibleId] = useState('none');
  const [planNotes, setPlanNotes] = useState('');
  
  const [savingRecord, setSavingRecord] = useState(false);
  const [savingPlan, setSavingPlan] = useState(false);
  const [savingQuickRecordTime, setSavingQuickRecordTime] = useState(null);
  const [recordClientRequestId, setRecordClientRequestId] = useState(() => createClientRequestId('feeding-record'));
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  useEffect(() => {
    if (animalId) {
      fetchRecords();
      fetchPlan();
      fetchSummary();
      fetchResponsibleUsers();
    }
  }, [animalId, filterStartDate, filterEndDate, filterStatus]);

  const getUserDisplayName = (targetUser) => (
    targetUser?.name || targetUser?.full_name || targetUser?.email || ''
  );

  const getResponsibleName = (targetResponsibleId) => {
    const responsible = responsibleUsers.find((item) => item.id === targetResponsibleId);
    return getUserDisplayName(responsible);
  };

  const fetchResponsibleUsers = async () => {
    try {
      const response = await axios.get(`${API}/api/feeding-responsibles`, {
        params: { animal_id: animalId }
      });
      setResponsibleUsers(response.data || []);
    } catch (error) {
      console.error('Error fetching feeding responsibles:', error);
    }
  };

  const fetchRecords = async () => {
    try {
      const params = new URLSearchParams();
      if (filterStartDate) params.append('start_date', filterStartDate);
      if (filterEndDate) params.append('end_date', filterEndDate);
      if (filterStatus && filterStatus !== 'all') {
        params.append('status', filterStatus);
      }
      
      const response = await axios.get(`${API}/api/animals/${animalId}/feeding-records?${params.toString()}`);
      setRecords(response.data);
    } catch (error) {
      console.error('Error fetching feeding records:', error);
    }
  };

  const fetchPlan = async () => {
    try {
      const response = await axios.get(`${API}/api/animals/${animalId}/feeding-plan`);
      setPlan(response.data);
      const fasting = response.data?.fasting_schedules?.[0];
      setPlanFoodType(response.data?.food_type || '');
      setPlanQuantity(response.data?.quantity || '');
      setPlanUnit(response.data?.unit || 'g');
      setPlanForm(response.data?.form || 'Sólida');
      setPlanFrequency(response.data?.daily_frequency ? String(response.data.daily_frequency) : '3');
      setPlanSchedules((response.data?.schedules || []).map((item) => item.time));
      setPlanResponsibleId(response.data?.responsible_id || 'none');
      setPlanNotes(response.data?.clinical_notes || '');
      setFastingStartAt(fasting?.start_at ? fasting.start_at.slice(0, 16) : '');
      setFastingEndAt(fasting?.end_at ? fasting.end_at.slice(0, 16) : '');
      setFastingProcedureName(fasting?.procedure_name || '');
      setFastingReason(fasting?.reason || '');
    } catch (error) {
      console.error('Error fetching feeding plan:', error);
    }
  };

  const fetchSummary = async () => {
    try {
      const today = appTodayDate();
      const response = await axios.get(`${API}/api/animals/${animalId}/feeding-summary?date=${today}`);
      setSummary(response.data);
    } catch (error) {
      console.error('Error fetching summary:', error);
    }
  };

  const handleCreateRecord = async (e) => {
    e.preventDefault();
    if (savingRecord) return;
    
    if (!foodType || !quantity) {
      toast.error('Tipo de alimento e quantidade são obrigatórios');
      return;
    }
    
    setSavingRecord(true);
    try {
      const payload = {
        animal_id: animalId,
        client_request_id: editingRecordId ? undefined : recordClientRequestId,
        date: recordDate || undefined,
        time: recordTime || undefined,
        food_type: foodType,
        quantity: parseFloat(quantity),
        unit,
        form,
        administration_type: administrationType,
        status,
        responsible_id: responsibleId || undefined,
        responsible_name: getResponsibleName(responsibleId) || undefined,
        observations: observations || null
      };
      const response = editingRecordId
        ? await axios.put(`${API}/api/feeding-records/${editingRecordId}`, payload)
        : await axios.post(`${API}/api/feeding-records`, payload);

      if (!editingRecordId && recordMediaFile) {
        const formData = new FormData();
        formData.append('file', recordMediaFile);
        formData.append('caption', 'Foto do alimento');
        await axios.post(`${API}/api/feeding-records/${response.data.id}/media`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      }
      
      toast.success(editingRecordId ? 'Registro de alimentação atualizado!' : 'Registro de alimentação criado!');
      if (!editingRecordId) {
        setRecordClientRequestId(createClientRequestId('feeding-record'));
      }
      closeRecordDialog();
      fetchRecords();
      fetchSummary();
    } catch (error) {
      toast.error(getApiErrorMessage(error, editingRecordId ? 'Erro ao atualizar registro' : 'Erro ao criar registro'));
    } finally {
      setSavingRecord(false);
    }
  };

  const handleCreatePlan = async (e) => {
    e.preventDefault();
    if (savingPlan) return;
    
    if (!planFoodType || !planQuantity || !planFrequency) {
      toast.error('Preencha todos os campos obrigatórios');
      return;
    }
    
    setSavingPlan(true);
    try {
      await axios.post(`${API}/api/feeding-plans`, {
        animal_id: animalId,
        food_type: planFoodType,
        quantity: parseFloat(planQuantity),
        unit: planUnit,
        form: planForm,
        daily_frequency: parseInt(planFrequency),
        schedules: planSchedules.slice(0, parseInt(planFrequency)),
        responsible_id: planResponsibleId !== 'none' ? planResponsibleId : undefined,
        fasting_schedules: fastingStartAt && fastingEndAt && fastingProcedureName ? [{
          start_at: buildAppDateTimeIso(fastingStartAt.slice(0, 10), fastingStartAt.slice(11, 16)),
          end_at: buildAppDateTimeIso(fastingEndAt.slice(0, 10), fastingEndAt.slice(11, 16)),
          procedure_name: fastingProcedureName,
          reason: fastingReason || null
        }] : [],
        clinical_notes: planNotes || null
      });
      
      toast.success('Plano alimentar criado!');
      setShowPlanForm(false);
      fetchPlan();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao criar plano');
    } finally {
      setSavingRecord(false);
    }
  };

  const handleQuickRecord = async (scheduleTime) => {
    if (!plan || savingQuickRecordTime) return;
    
    setSavingQuickRecordTime(scheduleTime);
    try {
      await axios.post(`${API}/api/feeding-records`, {
        animal_id: animalId,
        client_request_id: createClientRequestId(`feeding-quick-${scheduleTime}`),
        time: scheduleTime,
        food_type: plan.food_type,
        quantity: plan.quantity,
        unit: plan.unit,
        form: plan.form,
        administration_type: 'Voluntária',
        status: 'Aceita',
        responsible_id: plan.responsible_id || user?.id || undefined,
        responsible_name: plan.responsible_name || user?.name || user?.full_name || undefined
      });
      
      toast.success('Alimentação registrada!');
      fetchRecords();
      fetchSummary();
    } catch (error) {
      toast.error('Erro ao registrar');
    } finally {
      setSavingQuickRecordTime(null);
    }
  };

  const handleDownloadPdf = async (type = 'all') => {
    setDownloadingPdf(true);
    try {
      let url = `${API}/api/animals/${animalId}/feeding-pdf`;
      if (type === 'daily') {
        url = `${API}/api/animals/${animalId}/feeding-pdf/daily`;
        if (filterStartDate) url += `?date=${filterStartDate}`;
      } else if (filterStartDate || filterEndDate) {
        const query = new URLSearchParams();
        if (filterStartDate) query.append('start_date', filterStartDate);
        if (filterEndDate) query.append('end_date', filterEndDate);
        url += `?${query.toString()}`;
      }
      
      const response = await axios.get(url, { responseType: 'blob' });
      
      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = blobUrl;
      link.setAttribute('download', `alimentacao_${animalId}_${appTodayDate()}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('PDF gerado com sucesso!');
    } catch (error) {
      toast.error('Erro ao gerar PDF');
    } finally {
      setDownloadingPdf(false);
    }
  };

  const resetRecordForm = () => {
    setEditingRecordId(null);
    setFoodType('');
    setQuantity('');
    setUnit('g');
    setForm('Sólida');
    setAdministrationType('Voluntária');
    setStatus('Aceita');
    setResponsibleId(plan?.responsible_id || user?.id || '');
    setObservations('');
    setRecordDate('');
    setRecordTime('');
    setRecordMediaFile(null);
  };

  const openCreateRecordDialog = () => {
    resetRecordForm();
    setRecordClientRequestId(createClientRequestId('feeding-record'));
    setShowNewRecordForm(true);
  };

  const closeRecordDialog = () => {
    setShowNewRecordForm(false);
    resetRecordForm();
  };

  const openEditRecordDialog = (record) => {
    setEditingRecordId(record.id);
    setFoodType(record.food_type || '');
    setQuantity(String(record.quantity ?? ''));
    setUnit(record.unit || 'g');
    setForm(record.form || 'Sólida');
    setAdministrationType(record.administration_type || 'Voluntária');
    setStatus(record.status || 'Aceita');
    setResponsibleId(record.responsible_id || '');
    setObservations(record.observations || '');
    setRecordDate(record.date || '');
    setRecordTime(record.time || '');
    setRecordMediaFile(null);
    setShowNewRecordForm(true);
  };

  const handleOpenFeedingMedia = async (event, media) => {
    event.preventDefault();
    event.stopPropagation();
    try {
      await openProtectedFile(media.file_url, media.filename);
    } catch (error) {
      toast.error(error.message || 'Erro ao abrir mídia');
    }
  };

  const getStatusIcon = (statusValue) => {
    switch (statusValue) {
      case 'Aceita': return <CheckCircle2 className="w-4 h-4 text-green-600" />;
      case 'Recusada': return <XCircle className="w-4 h-4 text-red-600" />;
      case 'Parcial': return <MinusCircle className="w-4 h-4 text-yellow-600" />;
      default: return <Clock className="w-4 h-4 text-gray-500" />;
    }
  };

  const getStatusBadge = (statusValue) => {
    const statusConfig = FEEDING_STATUS.find(s => s.value === statusValue);
    return statusConfig ? statusConfig.color : 'bg-gray-100 text-gray-700';
  };

  const canEditPlan = hasPermission(user, 'feeding.plan.manage');
  const canEditRecord = ['administrador', 'programador'].includes(user?.role);
  const canDeleteRecord = hasPermission(user, 'records.delete');
  const canDeletePlan = hasPermission(user, 'records.delete');
  const canDeleteMedia = ['administrador', 'programador'].includes(user?.role);

  const handleDeleteMedia = async (recordId, media) => {
    if (!media?.file_url) return;
    if (!window.confirm('Excluir esta foto do registro alimentar?')) return;
    try {
      await axios.delete(`${API}/api/feeding-records/${recordId}/media`, {
        params: { file_url: media.file_url }
      });
      toast.success('Foto excluída');
      fetchRecords();
      fetchSummary();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao excluir foto');
    }
  };

  const availableSummaryUnits = useMemo(() => {
    const recordUnits = records.map((r) => String(r.unit || '').toLowerCase()).filter(Boolean);
    const defaultUnits = UNITS.map((u) => u.value.toLowerCase());
    return [...new Set([...defaultUnits, ...recordUnits])];
  }, [records]);

  const totalBySelectedUnit = useMemo(() => {
    return records
      .filter((r) => (r.status === 'Aceita' || r.status === 'Parcial') && String(r.unit || '').toLowerCase() === summaryUnit.toLowerCase())
      .reduce((acc, r) => acc + (Number(r.quantity) || 0), 0);
  }, [records, summaryUnit]);

  const handleDeleteRecord = async (recordId) => {
    try {
      await axios.delete(`${API}/api/feeding-records/${recordId}`);
      toast.success('Registro de alimentação excluído');
      fetchRecords();
      fetchSummary();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao excluir registro');
    }
  };

  const handleDeletePlan = async () => {
    if (!plan?.id) return;
    if (!window.confirm('Excluir este plano alimentar? A ação ficará registrada na auditoria.')) return;
    try {
      await axios.delete(`${API}/api/feeding-plans/${plan.id}`);
      toast.success('Plano alimentar excluído');
      setPlan(null);
      fetchPlan();
      fetchSummary();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao excluir plano alimentar');
    }
  };

  return (
    <div className="space-y-6" data-testid="feeding-tab">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-xl md:text-2xl font-bold text-emerald-800 flex items-center gap-2">
            <UtensilsCrossed className="w-6 h-6" />
            Alimentação
          </h2>
          <p className="text-sm text-gray-600">Registros e plano alimentar do animal</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            onClick={() => handleDownloadPdf('all')}
            variant="outline"
            disabled={downloadingPdf}
            className="border-emerald-600 text-emerald-600 hover:bg-emerald-50 min-h-[44px]"
            data-testid="download-pdf-btn"
          >
            {downloadingPdf ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
            PDF
          </Button>
          <Button
            onClick={() => setShowNewRecordForm(true)}
            className="bg-emerald-600 hover:bg-emerald-700 min-h-[44px]"
            data-testid="new-feeding-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Registrar Alimentação
          </Button>
        </div>
      </div>

      {/* Daily Summary Card */}
      {summary && (
        <Card className="border-emerald-200 bg-gradient-to-r from-emerald-50 to-teal-50">
          <CardContent className="pt-4">
            <div className="flex flex-wrap items-center gap-4 md:gap-8">
              <div className="text-center">
                <p className="text-3xl font-bold text-emerald-700">{summary.total_records}</p>
                <p className="text-xs text-gray-600">Registros Hoje</p>
              </div>
              <div className="text-center">
                <p className="text-3xl font-bold text-green-600">{summary.accepted_count}</p>
                <p className="text-xs text-gray-600">Aceitas</p>
              </div>
              <div className="text-center">
                <p className="text-3xl font-bold text-yellow-600">{summary.partial_count}</p>
                <p className="text-xs text-gray-600">Parciais</p>
              </div>
              <div className="text-center">
                <p className="text-3xl font-bold text-red-600">{summary.refused_count}</p>
                <p className="text-xs text-gray-600">Recusadas</p>
              </div>
              <div className="text-center border-l pl-4 md:pl-8">
                <p className="text-3xl font-bold text-blue-600">{totalBySelectedUnit.toFixed(1)}</p>
                <p className="text-xs text-gray-600">Total Ingerido ({summaryUnit})</p>
                <Select value={summaryUnit} onValueChange={setSummaryUnit}>
                  <SelectTrigger className="h-8 mt-2 w-28">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {availableSummaryUnits.map((u) => (
                      <SelectItem key={u} value={u}>{u}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Active Feeding Plan */}
      <Card className="border-blue-200">
        <CardHeader className="pb-2">
          <div className="flex justify-between items-center">
            <CardTitle className="text-lg text-blue-800 flex items-center gap-2">
              <ClipboardList className="w-5 h-5" />
              Plano Alimentar
            </CardTitle>
            <div className="flex flex-wrap gap-2">
              {canEditPlan && (
                <Button
                  onClick={() => setShowPlanForm(true)}
                  variant="outline"
                  size="sm"
                  className="border-blue-600 text-blue-600 hover:bg-blue-50"
                  data-testid="edit-plan-btn"
                >
                  {plan ? <Edit className="w-4 h-4 mr-1" /> : <Plus className="w-4 h-4 mr-1" />}
                  {plan ? 'Editar' : 'Criar'} Plano
                </Button>
              )}
              {plan && canDeletePlan && (
                <Button
                  type="button"
                  onClick={handleDeletePlan}
                  variant="outline"
                  size="sm"
                  className="border-red-300 text-red-600 hover:bg-red-50"
                  data-testid="delete-feeding-plan-btn"
                >
                  <Trash2 className="w-4 h-4 mr-1" />
                  Excluir Plano
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {plan ? (
            <div className="space-y-4">
              <div className="grid grid-cols-1 gap-4 text-sm sm:grid-cols-2 md:grid-cols-4">
                <div>
                  <p className="text-gray-500">Responsável direcionado</p>
                  <p className="font-semibold">{plan.responsible_name || 'Não definido'}</p>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-4 text-sm sm:grid-cols-2 md:grid-cols-4">
                <div>
                  <p className="text-gray-500">Alimento</p>
                  <p className="font-semibold">{plan.food_type}</p>
                </div>
                <div>
                  <p className="text-gray-500">Quantidade</p>
                  <p className="font-semibold">{plan.quantity} {plan.unit}</p>
                </div>
                <div>
                  <p className="text-gray-500">Forma</p>
                  <p className="font-semibold flex items-center gap-1">
                    {plan.form === 'Assistida/Sonda' && <Syringe className="w-4 h-4 text-blue-500" />}
                    {plan.form}
                  </p>
                </div>
                <div>
                  <p className="text-gray-500">Frequência</p>
                  <p className="font-semibold">{plan.daily_frequency}x ao dia</p>
                </div>
              </div>

              {plan.fasting_schedules?.length > 0 && (
                <div className="rounded-lg border border-orange-200 bg-orange-50 p-3 text-sm">
                  <p className="font-semibold text-orange-800">Jejum programado</p>
                  {plan.fasting_schedules.map((fasting, idx) => (
                    <p key={idx} className="text-orange-700">
                      {fasting.procedure_name}: {new Date(fasting.start_at).toLocaleString('pt-BR')} até {new Date(fasting.end_at).toLocaleString('pt-BR')}
                    </p>
                  ))}
                </div>
              )}
              
              {plan.schedules && plan.schedules.length > 0 && (
                <div>
                  <p className="text-gray-500 text-sm mb-2">Horários programados:</p>
                  <div className="flex flex-wrap gap-2">
                    {plan.schedules.map((schedule, idx) => (
                      <Button
                        key={idx}
                        variant="outline"
                        size="sm"
                        onClick={() => handleQuickRecord(schedule.time)}
                        disabled={Boolean(savingQuickRecordTime)}
                        className="border-emerald-300 hover:bg-emerald-50"
                        title="Clique para registrar"
                        data-testid={`quick-record-${idx}`}
                      >
                        <Clock className="w-4 h-4 mr-1" />
                        {savingQuickRecordTime === schedule.time ? 'Salvando...' : schedule.time}
                      </Button>
                    ))}
                  </div>
                  <p className="text-xs text-gray-400 mt-1">Clique no horário para registro rápido</p>
                </div>
              )}
              
              {plan.clinical_notes && (
                <div className="bg-yellow-50 p-3 rounded-lg text-sm">
                  <p className="text-gray-700"><strong>Obs. Clínicas:</strong> {plan.clinical_notes}</p>
                </div>
              )}
            </div>
          ) : (
            <p className="text-gray-500 text-sm py-4 text-center">
              Nenhum plano alimentar cadastrado.
              {canEditPlan && ' Clique em "Criar Plano" para configurar.'}
            </p>
          )}
        </CardContent>
      </Card>

      {/* Filters */}
      <Card>
        <CardContent className="pt-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <Label className="flex items-center gap-2 mb-2">
                <Calendar className="w-4 h-4" /> Data inicial
              </Label>
              <Input
                type="date"
                value={filterStartDate}
                onChange={(e) => setFilterStartDate(e.target.value)}
                data-testid="filter-start-date"
              />
            </div>
            <div>
              <Label className="flex items-center gap-2 mb-2">
                <Calendar className="w-4 h-4" /> Data final
              </Label>
              <Input
                type="date"
                value={filterEndDate}
                onChange={(e) => setFilterEndDate(e.target.value)}
                data-testid="filter-end-date"
              />
            </div>
            <div>
              <Label className="flex items-center gap-2 mb-2">
                <Filter className="w-4 h-4" /> Status
              </Label>
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger data-testid="filter-status">
                  <SelectValue placeholder="Todos" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  {FEEDING_STATUS.map(s => (
                    <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end">
              <Button
                variant="outline"
                onClick={() => { setFilterStartDate(''); setFilterEndDate(''); setFilterStatus('all'); }}
                className="w-full md:w-auto"
              >
                Limpar Filtros
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Records List */}
      <div className="space-y-4" data-testid="feeding-records-list">
        {records.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <UtensilsCrossed className="w-12 h-12 mx-auto text-gray-300 mb-4" />
              <p className="text-gray-500">Nenhum registro de alimentação encontrado</p>
              <Button
                onClick={() => setShowNewRecordForm(true)}
                className="mt-4 bg-emerald-600"
              >
                <Plus className="w-4 h-4 mr-2" />
                Criar Primeiro Registro
              </Button>
            </CardContent>
          </Card>
        ) : (
          records.map((record) => (
            <Card 
              key={record.id} 
              className={`border-l-4 ${
                record.status === 'Recusada' ? 'border-l-red-500' :
                record.status === 'Parcial' ? 'border-l-yellow-500' :
                record.form === 'Assistida/Sonda' ? 'border-l-blue-500' :
                'border-l-emerald-500'
              }`}
              data-testid={`feeding-record-${record.id}`}
            >
              <CardContent className="pt-4">
                <div className="flex flex-col md:flex-row justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex flex-wrap items-center gap-2 mb-2">
                      <Badge className={getStatusBadge(record.status)}>
                        {getStatusIcon(record.status)}
                        <span className="ml-1">{record.status}</span>
                      </Badge>
                      {record.form === 'Assistida/Sonda' && (
                        <Badge className="bg-blue-100 text-blue-700">
                          <Syringe className="w-3 h-3 mr-1" />
                          Assistida
                        </Badge>
                      )}
                      {record.administration_type && (
                        <Badge className={record.administration_type === 'Forçada' ? 'bg-orange-100 text-orange-700' : 'bg-emerald-100 text-emerald-700'}>
                          {record.administration_type}
                        </Badge>
                      )}
                      <span className="text-sm text-gray-500">
                        {record.date} às {record.time}
                      </span>
                    </div>
                    
                    <div className="grid grid-cols-1 gap-3 text-sm sm:grid-cols-2 md:grid-cols-5">
                      <div>
                        <p className="text-gray-500">Alimento</p>
                        <p className="font-semibold">{record.food_type}</p>
                      </div>
                      <div>
                        <p className="text-gray-500">Quantidade</p>
                        <p className="font-semibold">{record.quantity} {record.unit}</p>
                      </div>
                      <div>
                        <p className="text-gray-500">Forma</p>
                        <p className="font-semibold">{record.form}</p>
                      </div>
                      <div>
                        <p className="text-gray-500">Administração</p>
                        <p className="font-semibold">{record.administration_type || 'Voluntária'}</p>
                      </div>
                      <div>
                        <p className="text-gray-500">Responsável direcionado</p>
                        <p className="font-semibold">{record.responsible_name || 'Não definido'}</p>
                      </div>
                    </div>
                    {(record.created_by_name || record.registered_by_name) && (
                      <p className="mt-2 text-xs text-gray-500">
                        Executado/registrado por {record.created_by_name || record.registered_by_name}
                      </p>
                    )}
                    
                    {record.observations && (
                      <div className="mt-3 p-2 bg-gray-50 rounded text-sm">
                        <strong>Obs:</strong> {record.observations}
                      </div>
                    )}

                    {record.media?.length > 0 && (
                      <div className="mt-3 rounded border border-emerald-100 bg-emerald-50 p-3">
                        <p className="mb-2 text-sm font-medium text-emerald-900">Fotos do alimento</p>
                        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                          {record.media.map((media, index) => (
                            <div
                              key={`${record.id}-feeding-media-${index}`}
                              className="flex items-center justify-between gap-2 rounded border border-emerald-200 bg-white p-2 text-left text-sm hover:bg-emerald-50"
                            >
                              <button
                                type="button"
                                onClick={(event) => handleOpenFeedingMedia(event, media)}
                                className="flex min-w-0 flex-1 items-center gap-2 text-left"
                              >
                                <ProtectedMediaThumbnail
                                  media={media}
                                  alt={media.caption || media.filename || `Foto ${index + 1}`}
                                  className="h-14 w-20"
                                  fallbackClassName="h-14 w-20"
                                />
                                <span className="min-w-0">
                                  <span className="block truncate font-medium">{media.caption || media.filename || `Foto ${index + 1}`}</span>
                                  {media.observation && <span className="block truncate text-xs text-gray-500">{media.observation}</span>}
                                </span>
                              </button>
                              <span className="text-xs text-gray-500">
                                {media.uploaded_at ? new Date(media.uploaded_at).toLocaleString('pt-BR') : ''}
                                {media.uploaded_by_name ? ` • ${media.uploaded_by_name}` : ''}
                              </span>
                              {canDeleteMedia && (
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  className="h-8 px-2 text-red-600"
                                  onClick={() => handleDeleteMedia(record.id, media)}
                                  title="Excluir foto"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                  {(canEditRecord || canDeleteRecord) && (
                    <div className="flex items-start md:justify-end">
                      <div className="flex flex-col gap-2">
                        {canEditRecord && (
                          <Button
                            type="button"
                            variant="outline"
                            className="border-blue-300 text-blue-700 hover:bg-blue-50"
                            onClick={() => openEditRecordDialog(record)}
                            data-testid={`edit-feeding-record-${record.id}`}
                          >
                            <Edit className="w-4 h-4 mr-1" />
                            Editar
                          </Button>
                        )}
                        {canDeleteRecord && (
                          <Button
                            type="button"
                            variant="outline"
                            className="text-red-600 border-red-300 hover:bg-red-50"
                            onClick={() => handleDeleteRecord(record.id)}
                            data-testid={`delete-feeding-record-${record.id}`}
                          >
                            <Trash2 className="w-4 h-4 mr-1" />
                            Excluir
                          </Button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* New Record Dialog */}
      <Dialog open={showNewRecordForm} onOpenChange={setShowNewRecordForm}>
        <DialogContent className="w-[calc(100vw-1.5rem)] max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-emerald-800 flex items-center gap-2">
              <UtensilsCrossed className="w-5 h-5" />
              {editingRecordId ? 'Editar Alimentação' : 'Registrar Alimentação'}
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleCreateRecord} className="space-y-4">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <Label>Data</Label>
                <Input
                  type="date"
                  value={recordDate}
                  onChange={(e) => setRecordDate(e.target.value)}
                  placeholder="Hoje"
                  data-testid="record-date"
                />
              </div>
              <div>
                <Label>Horário</Label>
                <Input
                  type="time"
                  value={recordTime}
                  onChange={(e) => setRecordTime(e.target.value)}
                  placeholder="Agora"
                  data-testid="record-time"
                />
              </div>
            </div>
            
            <div>
              <Label>Tipo de Alimento *</Label>
              <Select value={foodType} onValueChange={setFoodType}>
                <SelectTrigger data-testid="food-type-select">
                  <SelectValue placeholder="Selecione" />
                </SelectTrigger>
                <SelectContent>
                  {FOOD_TYPES.map(type => (
                    <SelectItem key={type} value={type}>{type}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <Label>Quantidade *</Label>
                <Input
                  type="number"
                  step="0.1"
                  value={quantity}
                  onChange={(e) => setQuantity(e.target.value)}
                  placeholder="Ex: 50"
                  data-testid="quantity-input"
                />
              </div>
              <div>
                <Label>Unidade</Label>
                <Select value={unit} onValueChange={setUnit}>
                  <SelectTrigger data-testid="unit-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {UNITS.map(u => (
                      <SelectItem key={u.value} value={u.value}>{u.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <Label>Forma de Alimentação</Label>
                <Select value={form} onValueChange={setForm}>
                  <SelectTrigger data-testid="form-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {FEEDING_FORMS.map(f => (
                      <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Administração</Label>
                <Select value={administrationType} onValueChange={setAdministrationType}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ADMINISTRATION_TYPES.map(item => (
                      <SelectItem key={item.value} value={item.value}>{item.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <Label>Status</Label>
                <Select value={status} onValueChange={setStatus}>
                  <SelectTrigger data-testid="status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {FEEDING_STATUS.map(s => (
                      <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Responsável direcionado</Label>
                <Select value={responsibleId || 'none'} onValueChange={(value) => setResponsibleId(value === 'none' ? '' : value)}>
                  <SelectTrigger data-testid="feeding-responsible-select">
                    <SelectValue placeholder="Selecione" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Não definido</SelectItem>
                    {responsibleUsers.map((responsible) => (
                      <SelectItem key={responsible.id} value={responsible.id}>
                        {responsible.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Input
                  className="mt-2"
                  value={user?.full_name || user?.name || user?.email || ''}
                  disabled
                  aria-label="Executado por"
                  data-testid="feeding-responsible-input"
                />
              </div>
            </div>
            
            <div>
              <Label>Observações</Label>
              <Textarea
                value={observations}
                onChange={(e) => setObservations(e.target.value)}
                placeholder="Observações sobre a alimentação..."
                rows={3}
                data-testid="observations-input"
              />
            </div>

            <div>
              <Label>Foto do alimento (opcional)</Label>
              <CameraFileInput
                accept="image/*"
                onFileSelected={setRecordMediaFile}
                fileLabel="Escolher arquivo"
                cameraLabel="Tirar foto"
                disabled={savingRecord}
              />
              {recordMediaFile && (
                <p className="mt-1 text-xs text-gray-500">Selecionado: {recordMediaFile.name}</p>
              )}
            </div>
            
            <div className="flex flex-col gap-2 pt-4 sm:flex-row sm:justify-end">
              <Button
                type="button"
                variant="outline"
                onClick={closeRecordDialog}
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={savingRecord}
                className="bg-emerald-600 hover:bg-emerald-700"
                data-testid="submit-record-btn"
              >
                {savingRecord ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                {editingRecordId ? 'Salvar Alterações' : 'Registrar'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Feeding Plan Dialog */}
      <Dialog open={showPlanForm} onOpenChange={setShowPlanForm}>
        <DialogContent className="w-[calc(100vw-1.5rem)] max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-blue-800 flex items-center gap-2">
              <ClipboardList className="w-5 h-5" />
              {plan ? 'Editar' : 'Criar'} Plano Alimentar
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleCreatePlan} className="space-y-4">
            <div>
              <Label>Tipo de Alimento Padrão *</Label>
              <Select value={planFoodType} onValueChange={setPlanFoodType}>
                <SelectTrigger data-testid="plan-food-type">
                  <SelectValue placeholder="Selecione" />
                </SelectTrigger>
                <SelectContent>
                  {FOOD_TYPES.map(type => (
                    <SelectItem key={type} value={type}>{type}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <Label>Quantidade Padrão *</Label>
                <Input
                  type="number"
                  step="0.1"
                  value={planQuantity}
                  onChange={(e) => setPlanQuantity(e.target.value)}
                  placeholder="Ex: 50"
                  data-testid="plan-quantity"
                />
              </div>
              <div>
                <Label>Unidade</Label>
                <Select value={planUnit} onValueChange={setPlanUnit}>
                  <SelectTrigger data-testid="plan-unit">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {UNITS.map(u => (
                      <SelectItem key={u.value} value={u.value}>{u.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <Label>Forma de Alimentação</Label>
                <Select value={planForm} onValueChange={setPlanForm}>
                  <SelectTrigger data-testid="plan-form">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {FEEDING_FORMS.map(f => (
                      <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Frequência Diária *</Label>
                <Select value={planFrequency} onValueChange={(v) => {
                  setPlanFrequency(v);
                  // Adjust schedules array
                  const freq = parseInt(v);
                  const defaultTimes = ['06:00', '08:00', '10:00', '12:00', '14:00', '16:00', '18:00', '20:00'];
                  setPlanSchedules(defaultTimes.slice(0, freq));
                }}>
                  <SelectTrigger data-testid="plan-frequency">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[1, 2, 3, 4, 5, 6].map(n => (
                      <SelectItem key={n} value={String(n)}>{n}x ao dia</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div>
              <Label>Horários Sugeridos</Label>
              <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
                {planSchedules.map((time, idx) => (
                  <Input
                    key={idx}
                    type="time"
                    value={time}
                    onChange={(e) => {
                      const newSchedules = [...planSchedules];
                      newSchedules[idx] = e.target.value;
                      setPlanSchedules(newSchedules);
                    }}
                    data-testid={`plan-schedule-${idx}`}
                  />
                ))}
              </div>
            </div>
            
            <div>
              <Label>Responsável por alimentar</Label>
              <Select value={planResponsibleId} onValueChange={setPlanResponsibleId}>
                <SelectTrigger data-testid="plan-responsible">
                  <SelectValue placeholder="Selecione" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Não definido</SelectItem>
                  {responsibleUsers.map((responsible) => (
                    <SelectItem key={responsible.id} value={responsible.id}>
                      {responsible.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Observações Clínicas</Label>
              <Textarea
                value={planNotes}
                onChange={(e) => setPlanNotes(e.target.value)}
                placeholder="Recomendações do veterinário..."
                rows={3}
                data-testid="plan-notes"
              />
            </div>

            <div className="space-y-3 rounded-lg border border-orange-200 bg-orange-50 p-3">
              <Label className="text-orange-800">Jejum programado</Label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Input type="datetime-local" value={fastingStartAt} onChange={(e) => setFastingStartAt(e.target.value)} />
                <Input type="datetime-local" value={fastingEndAt} onChange={(e) => setFastingEndAt(e.target.value)} />
                <Input value={fastingProcedureName} onChange={(e) => setFastingProcedureName(e.target.value)} placeholder="Procedimento" />
                <Input value={fastingReason} onChange={(e) => setFastingReason(e.target.value)} placeholder="Motivo do jejum" />
              </div>
              <p className="text-xs text-orange-700">O sistema bloqueará registros de alimentação durante essa janela.</p>
            </div>
            
            <div className="flex flex-col gap-2 pt-4 sm:flex-row sm:justify-end">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowPlanForm(false)}
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={savingPlan}
                className="bg-blue-600 hover:bg-blue-700"
                data-testid="submit-plan-btn"
              >
                {savingPlan ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                {plan ? 'Atualizar' : 'Criar'} Plano
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
